import { IconName, ICONS } from '@/components/custom/Icons'
import CustomTooltip from '@/components/custom/tooltip/CustomTooltip'
import { useConfirm } from '@/components/modal/Modal'
import { Button as ShadcnButton, ButtonProps as ShadcnButtonProps } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { cva } from 'class-variance-authority'
import React from 'react'

export type ButtonVariant =
  | 'default'
  | 'destructive'
  | 'outline'
  | 'secondary'
  | 'ghost'
  | 'link'
  | 'proceed'
export interface CustomButtonProps extends Omit<ShadcnButtonProps, 'variant'> {
  // Qui puoi aggiungere props custom se necessario
  icon?: React.ReactNode | IconName | string
  tooltip?: string
  stopPropagation?: boolean
  confirm?: {
    title: string
    body?: string
  }
  text?: React.ReactNode
  variant?: ButtonVariant
  disabled?: boolean
}

// Estendi le varianti di shadcn
export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 transition-all duration-300 ease-in-out',
  {
    variants: {
      variant: {
        default: 'variant-default bg-primary text-primary-foreground hover:bg-primary-hover',
        destructive:
          'variant-destructive bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'variant-outline border border-input hover:bg-accent hover:text-accent-foreground',
        secondary:
          'variant-secondary bg-secondary text-secondary-foreground hover:bg-secondary-hover',
        ghost: 'variant-ghost hover:bg-accent hover:text-accent-foreground',
        link: 'variant-link underline-offset-4 hover:underline text-primary',
        proceed: 'bg-blue-600 text-primary-foreground shadow-sm hover:bg-blue-700',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

const CustomButton = React.forwardRef<HTMLButtonElement, CustomButtonProps>(
  (
    {
      className,
      variant,
      size,
      icon,
      tooltip,
      onClick,
      confirm,
      stopPropagation = false,
      text,
      disabled,
      ...props
    },
    ref
  ) => {
    const confirmModal = useConfirm()
    // Wrapper per l'onClick che include stopPropagation
    const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
      console.log('🚀 ~ handleClick ~ disabled:', disabled)

      if (disabled) return
      if (stopPropagation) {
        e.stopPropagation()
        e.preventDefault()
      }
      if (confirm) {
        const result = await confirmModal({
          title: confirm.title,
          body: confirm?.body || '',
        })
        if (!result) return
      }
      onClick?.(e)
    }

    let btn
    if (icon) {
      let iconClassName = ''
      if (text) {
        iconClassName = 'text-white !hover:text-primary !hover:bg-primary'
      } else {
        iconClassName = 'text-primary'
        if (variant === 'destructive') {
          iconClassName = 'text-destructive !hover:text-destructive !bg-white !hover:bg-white'
          variant = 'ghost'
        }
      }
      const IconComponent =
        typeof icon === 'string' ? ICONS({ className: iconClassName })[icon as IconName] : icon
      const _size = size || (text ? 'default' : 'icon')
      const _variant = variant || (text ? 'default' : 'ghost')
      const className = text ? 'custom-button' : 'custom-button button-icon'

      btn = (
        <ShadcnButton
          asChild
          ref={ref}
          variant={_variant}
          size={_size}
          disabled={disabled}
          onClick={handleClick}
          className={cn(
            'custom-button button-icon cursor-pointer',
            variant,
            buttonVariants({ variant: 'ghost', size: text ? 'default' : 'icon' }),
            className,
            text ? 'icon-text' : '',
            disabled && 'opacity-50 pointer-events-none'
          )}
          {...props}
        >
          <span className="flex items-center gap-2">
            {IconComponent} {text}
          </span>
        </ShadcnButton>
      )
    } else {
      btn = (
        <ShadcnButton
          ref={ref}
          variant={variant}
          size={size}
          disabled={disabled}
          onClick={handleClick}
          className={cn(
            'custom-button cursor-pointer',
            buttonVariants({ variant, size }),
            className,
            disabled && 'opacity-50 pointer-events-none'
          )}
          {...props}
        />
      )
    }

    if (tooltip) {
      return <CustomTooltip content={tooltip || ''}>{btn}</CustomTooltip>
    }
    return btn
  }
)
CustomButton.displayName = 'Button'

export { CustomButton }
